import {
  useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router';
import {
  AlarmClock, Bell, BellOff, BellRing, Bot, CalendarClock, CheckCheck, ChevronDown, Hourglass, ListChecks, ListFilter,
  MessageSquare, OctagonAlert, PlugZap, Reply, Sparkles, UserPlus,
  type LucideIcon,
} from 'lucide-react';
import type {
  ActionNotificationDTO, CommentNotificationDTO, DelegationNotificationDTO, DelegationResolution,
  ExtConnectionNotificationDTO, ExtServerNotificationDTO, ItemNotificationBase, ItemNotificationDTO, NotificationDTO,
  NotificationKind, NudgeNotificationDTO, OnboardingNotificationDTO, RunNotificationDTO,
} from '@contracts/activity';
import type { Id, Page } from '@contracts/common';
import { DEFAULT_PERSONA_NAME } from '@contracts/persona';
import { Button, buttonVariants } from '@/components/ui/button';
import { usePresence } from '@/hooks/use-presence';
import { labelOf, NOTIFICATION_KIND_LABEL, runKindLabel } from '@/lib/labels';
import { cn } from '@/lib/utils';
import { api } from '../../api/client';
import { formatDateTime } from '../../components/DateField';
import { StatusBadge, type BadgeTone } from '../../components/StatusBadge';
import { useAssistantOptional } from '../assistant/AssistantProvider';
import { ORDER_OFF } from '../assistant/missions/labels';
import { REMINDER } from '../assistant/onboarding/labels';
import { unfoldTray } from '../assistant/proactive/NudgeTray';
import { DecideButtons } from '../delegation/DecideButtons';
import { dayMonth, dueDate } from '../delegation/format';
import { RespondControls } from '../delegation/RespondControls';
import { groupInbox, type InboxCommentGroup, type InboxGroup } from '../workplace/groupInbox';

/**
 * One board member's private inbox, in the band. Three properties make it safe to leave open all day:
 *
 *  - it is nobody else's. Every route reads its owner from the session, so there is no reader to name and
 *    no parameter a browser could use to look into somebody else's inbox;
 *  - opening an entry is a read. It marks that one row read — at most once, and only if it was unread — and
 *    then follows the link. It writes no comment, no audit entry and no outbox intent;
 *  - it asks, rather than being told. There is no socket and no queue: the unread count is re-read every
 *    fifteen seconds while the tab is in front, and immediately when it comes back to the front.
 *
 * A notification about a task that has since been deleted opens the read-only history instead of the task,
 * because the row keeps saying what it said when it was raised.
 *
 * Assignments one person made under one big job within minutes of each other read as one row (spec §9,
 * `groupInbox`); opening it marks every unread member read in one write and opens that job, mine only. The
 * badge still counts each notification.
 *
 * An outside AI connection's heavy call waiting for its owner (`assistant_pending`, S2 §9.4, D71) is always a row of
 * its own: "**<persona>** đang chờ bạn xác nhận: <summary>", opening that action's confirm page. The server stops
 * showing it once the action is settled or expired.
 *
 * A background run of Hana's that needs its owner (`assistant_run`, S7 §9.4) is a row of its own too; it names what the
 * run waits on (`runNoticeText`) and opens Hana's tray, where the run's card is.
 *
 * The reader's own unfinished onboarding (`onboarding_reminder`, S8 §8.1) is a row of its own as well, opening `/welcome`.
 *
 * AI S9 (§7.1–§7.3): an assignment made with a delegation reads "{Bình} giao cho bạn {thẻ}" with the card's deadline,
 * and carries Nhận · Xin lùi hạn · Hỏi lại while the reader may still answer it; the delegator's deadline request
 * carries Chấp nhận / Từ chối, then its outcome; questions and comments quote their excerpt, and a person's unread
 * comments on one card read as one row. A press on one of those buttons is not a read: it opens nothing, marks
 * nothing and leaves the panel open; an Escape inside one of its popovers closes only the popover.
 */

export const UNREAD_COUNT_KEY = ['notifications', 'unread-count'] as const;
export const NOTIFICATION_POLL_MS = 15_000;

const PANEL_ID = 'hop-thu-thong-bao';

type KindTile = { icon: LucideIcon; tile: string };

/** The same four kinds as a picture beside the words; `NOTIFICATION_KIND_LABEL` is what says which kind it is. */
const KIND_ICON: Record<NotificationKind, KindTile> = {
  assignment: { icon: UserPlus, tile: 'bg-[hsl(var(--h-icon-bg-blue))] text-[hsl(var(--h-icon-blue))]' },
  blocked: { icon: OctagonAlert, tile: 'bg-[hsl(var(--h-icon-bg-pink))] text-[hsl(var(--h-icon-pink))]' },
  due_soon: { icon: Hourglass, tile: 'bg-[hsl(var(--h-warning-soft))] text-[hsl(var(--h-warning-fg))]' },
  overdue: { icon: AlarmClock, tile: 'bg-[hsl(var(--h-error-soft))] text-[hsl(var(--h-error-fg))]' },
  assistant_pending: { icon: Bot, tile: 'bg-[hsl(var(--h-icon-bg-violet))] text-[hsl(var(--h-icon-violet))]' },
  nudge: { icon: BellRing, tile: 'bg-[hsl(var(--h-icon-bg-violet))] text-[hsl(var(--h-icon-violet))]' },
  ext_connection_expired: { icon: PlugZap, tile: 'bg-[hsl(var(--h-warning-soft))] text-[hsl(var(--h-warning-fg))]' },
  ext_tools_review: { icon: ListChecks, tile: 'bg-[hsl(var(--h-icon-bg-blue))] text-[hsl(var(--h-icon-blue))]' },
  assistant_run: { icon: Bot, tile: 'bg-[hsl(var(--h-icon-bg-violet))] text-[hsl(var(--h-icon-violet))]' },
  onboarding_reminder: { icon: Sparkles, tile: 'bg-[hsl(var(--h-icon-bg-violet))] text-[hsl(var(--h-icon-violet))]' },
  // S9 §4.5: an answer to a delegation, a ruling on a deadline request, a comment on the reader's card.
  delegation_response: { icon: Reply, tile: 'bg-[hsl(var(--h-icon-bg-blue))] text-[hsl(var(--h-icon-blue))]' },
  delegation_decision: { icon: CalendarClock, tile: 'bg-[hsl(var(--h-warning-soft))] text-[hsl(var(--h-warning-fg))]' },
  comment: { icon: MessageSquare, tile: 'bg-[hsl(var(--h-icon-bg-blue))] text-[hsl(var(--h-icon-blue))]' },
};

/** A kind this build has no picture for keeps its row: a plain bell, while the words fall back to the code. */
const UNKNOWN_KIND: KindTile = {
  icon: Bell, tile: 'bg-[hsl(var(--h-muted))] text-[hsl(var(--h-muted-foreground))]',
};

const tileOf = (kind: string): KindTile =>
  (Object.hasOwn(KIND_ICON, kind) ? KIND_ICON[kind as NotificationKind] : UNKNOWN_KIND);

/** A live item opens in the Workplace; a deleted one only has a history left (spec §9, §11.1). */
const hrefOf = (row: Pick<ItemNotificationDTO, 'itemId' | 'itemDeleted'>): string =>
  (row.itemDeleted
    ? `/history/item/${encodeURIComponent(row.itemId)}`
    : `/workplace?item=${encodeURIComponent(row.itemId)}`);

/** A pending AI action opens its confirm page (S2 §9.3). */
const actionHref = (row: ActionNotificationDTO): string => `/connect/actions/${encodeURIComponent(row.actionId)}`;

/** A group opens its root with the "mine" chip on, so the reader lands on what was just handed to them. */
const groupHref = (group: InboxGroup): string => `/workplace?item=${encodeURIComponent(group.rootId)}&f=mine`;

/**
 * Who a group is from when its notices carry no frozen name — rows written before the name was frozen (ruling
 * T14-a). A frozen name is never looked up again, so a colleague since renamed or disabled reads as they were.
 */
const UNKNOWN_ACTOR = 'Một thành viên';

/** "… trong “<gốc>”" on a single row whose item sits under a bigger job; a root, or a legacy row, says nothing. */
const rootNote = (row: ItemNotificationBase): string | null =>
  (row.rootId !== null && row.rootTitle !== null && row.rootId !== row.itemId ? `trong “${row.rootTitle}”` : null);

const ROW = 'relative flex gap-3 px-4 py-3 transition-colors hover:bg-[hsl(var(--h-muted)/0.5)]';
const UNREAD_ROW = 'bg-[hsl(var(--h-primary)/0.04)]';
const TITLE_LINK = cn(
  'font-semibold leading-snug text-[hsl(var(--h-card-foreground))] no-underline wrap-anywhere',
  "after:absolute after:inset-0 after:content-[''] hover:text-[hsl(var(--h-primary))]",
);
const META = 'task-meta flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[hsl(var(--h-muted-foreground))]';

function KindTileIcon({ kind }: { kind: string }) {
  const tile = tileOf(kind);
  const KindIcon = tile.icon;
  return (
    <span className={cn('mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg', tile.tile)} aria-hidden="true">
      <KindIcon className="size-4" />
    </span>
  );
}

/**
 * One grouped row. The title link is stretched over the row like a single entry's; the toggle and the member
 * links sit above that stretch, so each is still its own target.
 */
function GroupRow({ group, actorName, onOpenGroup, onOpenRow }: {
  group: InboxGroup; actorName: string;
  onOpenGroup: (group: InboxGroup) => void; onOpenRow: (row: ItemNotificationDTO) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const listId = `nhom-thong-bao-${group.key}`;
  return (
    <li className={cn(ROW, group.unread && UNREAD_ROW)}>
      <KindTileIcon kind="assignment" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link to={groupHref(group)} onClick={() => { onOpenGroup(group); }} className={TITLE_LINK}>
          {`${actorName} giao bạn ${group.rows.length} việc trong “${group.rootTitle}”`}
        </Link>
        <span className={META}>
          <span>{labelOf(NOTIFICATION_KIND_LABEL, 'assignment')}</span>
          <span>{formatDateTime(group.createdAt)}</span>
          {group.unread && <StatusBadge tone="warn">Chưa đọc</StatusBadge>}
        </span>
        <Button
          type="button" variant="ghost" size="sm" aria-expanded={expanded} aria-controls={listId}
          className="relative z-10 self-start px-1 text-[hsl(var(--h-primary))]"
          onClick={() => setExpanded((value) => !value)}
        >
          <ChevronDown aria-hidden="true" className={cn('transition-transform', expanded && 'rotate-180')} />
          {expanded ? 'Thu gọn' : `Xem từng việc (${group.rows.length})`}
        </Button>
        {expanded && (
          <ul id={listId} className="relative z-10 flex flex-col gap-1 border-l border-[hsl(var(--h-border))] pl-3">
            {group.rows.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center gap-x-2 text-sm">
                <Link
                  to={hrefOf(row)} onClick={() => { onOpenRow(row); }}
                  className="wrap-anywhere text-[hsl(var(--h-card-foreground))] hover:text-[hsl(var(--h-primary))]"
                >
                  {row.title}
                </Link>
                {row.readAt === null && <span className="text-[hsl(var(--h-muted-foreground))]">· Chưa đọc</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

/** A pending AI action: the persona in bold, then what it asks. Opening it marks it read like any single row. */
function ActionRow({ row, onOpen }: { row: ActionNotificationDTO; onOpen: (row: ActionNotificationDTO) => void }) {
  return (
    <li className={cn(ROW, row.readAt === null && UNREAD_ROW)}>
      <KindTileIcon kind={row.kind} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link to={actionHref(row)} onClick={() => { onOpen(row); }} className={TITLE_LINK}>
          <strong className="font-bold">{row.persona}</strong>
          <span className="font-normal">{` đang chờ bạn xác nhận: ${row.summary}`}</span>
        </Link>
        <span className={META}>
          <span>{labelOf(NOTIFICATION_KIND_LABEL, row.kind)}</span>
          <span>{formatDateTime(row.createdAt)}</span>
          {row.readAt === null && <StatusBadge tone="warn">Chưa đọc</StatusBadge>}
        </span>
      </div>
    </li>
  );
}

/** A colleague's nudge (S3 §7.7, L2 D64): the sender in bold, ✦ (S8 §6.2: assisted, never whose persona), then the note. Opens its task like an
 *  item notice, names the task under the line, and is always a row of its own. */
function NudgeRow({ row, onOpen }: { row: NudgeNotificationDTO; onOpen: (row: NudgeNotificationDTO) => void }) {
  return (
    <li className={cn(ROW, row.readAt === null && UNREAD_ROW)}>
      <KindTileIcon kind={row.kind} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link to={hrefOf(row)} onClick={() => { onOpen(row); }} className={TITLE_LINK}>
          <strong className="font-bold">{row.actorName}</strong>
          {/* S8 §6.2: "Bình ✦" — the sender used an assistant; whose persona is never the recipient's to read. */}
          <span aria-hidden="true" className="font-extrabold text-[hsl(var(--h-icon-violet))]">{' ✦'}</span>
          <span className="sr-only">{' (qua trợ lý)'}</span>
          {/* Mockup Màn 5: "**Bình ✦ nhắc:** note". */}
          <span className="font-bold">{' nhắc:'}</span>
          <span className="font-normal">{` ${row.note}`}</span>
        </Link>
        {/* Which task, as it was named when the nudge was sent: the note alone may not say. */}
        <span className="text-sm text-[hsl(var(--h-muted-foreground))] wrap-anywhere">{row.title}</span>
        <span className={META}>
          <span>{labelOf(NOTIFICATION_KIND_LABEL, row.kind)}</span>
          <span>{formatDateTime(row.createdAt)}</span>
          {row.readAt === null && <StatusBadge tone="warn">Chưa đọc</StatusBadge>}
          {row.itemDeleted && <span>Việc đã xóa</span>}
        </span>
      </div>
    </li>
  );
}

/**
 * S7 §9.4, D83, D113: what a background run waits on, the most pressing first — its goal card gone, cards to confirm, its question, a mission it
 * completed, else why it stopped — then the mission's title or the order's name.
 */
export function runNoticeText(row: RunNotificationDTO, persona: string): string {
  // L4 P5: an order that went off says why, whatever else its run needs.
  const what = row.orderOff != null ? ORDER_OFF.notice(row.orderOff)
    : row.stale ? `${persona} tạm dừng nhiệm vụ vì thẻ mục tiêu đã bị xoá`
    : row.pending > 0 ? `${persona} cần anh xác nhận ${row.pending} việc`
    : row.asked ? `${persona} có câu hỏi`
      : row.completed ? `${persona} đã xong nhiệm vụ`
        : `${persona} dừng việc`;
  return `${what} · ${row.title}`;
}

/** A background run of Hana's that needs its owner (S7 §9.4): opens Hana's tray, where the run's card is. Its own row. */
function RunRow({ row, onOpen }: { row: RunNotificationDTO; onOpen: (row: RunNotificationDTO) => void }) {
  // C-F6: the reader's own persona (the run is theirs); the default name outside the assistant's provider.
  const persona = useAssistantOptional()?.prefs.personaName ?? DEFAULT_PERSONA_NAME;
  return (
    <li className={cn(ROW, row.readAt === null && UNREAD_ROW)}>
      <KindTileIcon kind={row.kind} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <button type="button" onClick={() => { onOpen(row); }} className={cn(TITLE_LINK, 'text-left')}>
          {runNoticeText(row, persona)}
        </button>
        <span className={META}>
          <span>{runKindLabel(persona)}</span>
          <span>{formatDateTime(row.createdAt)}</span>
          {row.readAt === null && <StatusBadge tone="warn">Chưa đọc</StatusBadge>}
        </span>
      </div>
    </li>
  );
}

/** Where the onboarding reminder sends its reader: the wizard, which opens at their step (S8 D131). */
const WELCOME_HREF = '/welcome';

/**
 * S8 D128, mockup Màn 2: "Hoàn tất cài đặt trợ lý", the steps left with about how long they take, and a primary
 * "Tiếp tục ở bước {n}". The title is stretched over the row; the button sits above that stretch. Either one marks the
 * row read and opens `/welcome`. It names no persona.
 */
function OnboardingRow({ row, onOpen }: {
  row: OnboardingNotificationDTO; onOpen: (row: OnboardingNotificationDTO) => void;
}) {
  return (
    <li className={cn(ROW, row.readAt === null && UNREAD_ROW)}>
      <KindTileIcon kind={row.kind} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link to={WELCOME_HREF} onClick={() => { onOpen(row); }} className={TITLE_LINK}>{REMINDER.title}</Link>
        <span className="text-sm text-[hsl(var(--h-muted-foreground))] wrap-anywhere">{REMINDER.body(row.remaining)}</span>
        <Link
          to={WELCOME_HREF} onClick={() => { onOpen(row); }}
          className={cn(buttonVariants({ size: 'sm' }), 'relative z-10 self-start no-underline')}
        >
          {REMINDER.resume(row.step)}
        </Link>
        <span className={META}>
          <span>{labelOf(NOTIFICATION_KIND_LABEL, row.kind)}</span>
          <span>{formatDateTime(row.createdAt)}</span>
          {row.readAt === null && <StatusBadge tone="warn">Chưa đọc</StatusBadge>}
        </span>
      </div>
    </li>
  );
}

type ExtNotificationDTO = ExtConnectionNotificationDTO | ExtServerNotificationDTO;

/** An outside MCP connection that needs a new sign-in, or a server whose tools wait for review (S6 §11.4): the
 *  server's title, opening the page that fixes it (the server's own `link`). Always a row of its own. */
function ExtRow({ row, onOpen }: { row: ExtNotificationDTO; onOpen: (row: ExtNotificationDTO) => void }) {
  return (
    <li className={cn(ROW, row.readAt === null && UNREAD_ROW)}>
      <KindTileIcon kind={row.kind} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link to={row.link} onClick={() => { onOpen(row); }} className={TITLE_LINK}>{row.title}</Link>
        <span className={META}>
          <span>{labelOf(NOTIFICATION_KIND_LABEL, row.kind)}</span>
          <span>{formatDateTime(row.createdAt)}</span>
          {row.readAt === null && <StatusBadge tone="warn">Chưa đọc</StatusBadge>}
        </span>
      </div>
    </li>
  );
}

/** §7.3: what a deleted comment's excerpt reads (the server's `COMMENT_DELETED_EXCERPT`); never quoted. */
const DELETED_EXCERPT = 'Bình luận đã bị xoá';

/** D85: a deadline request's outcome, read-only once it is decided. */
const RESOLUTION_WORD: Record<DelegationResolution, { word: string; tone: BadgeTone }> = {
  accepted: { word: 'Đã chấp nhận', tone: 'good' },
  declined: { word: 'Đã từ chối', tone: 'neutral' },
  withdrawn: { word: 'Đã rút', tone: 'neutral' },
};

/** The person in bold, then "✦" when an assistant carried it (S8 §6.2: never whose persona). */
function Actor({ name, assisted }: { name: string | null; assisted: boolean }) {
  return (
    <>
      <strong className="font-bold">{name ?? UNKNOWN_ACTOR}</strong>
      {assisted && (
        <>
          <span aria-hidden="true" className="font-extrabold text-[hsl(var(--h-icon-violet))]">{' ✦'}</span>
          <span className="sr-only">{' (qua trợ lý)'}</span>
        </>
      )}
    </>
  );
}

/**
 * A comment's or a question's words under the line, quoted; a deleted one says so, unquoted. "Deleted" comes from the
 * server's flag, never from the words, which are peer text and may spell the marker (lens 2 MINOR-1).
 */
function Excerpt({ text, deleted = false }: { text: string; deleted?: boolean }) {
  return (
    <span className={cn('text-sm text-[hsl(var(--h-muted-foreground))] wrap-anywhere', deleted && 'italic')}>
      {deleted ? DELETED_EXCERPT : `“${text}”`}
    </span>
  );
}

function RowMeta({ row, children }: {
  row: Pick<ItemNotificationBase, 'createdAt' | 'readAt' | 'itemDeleted'> & { kind: string }; children?: ReactNode;
}) {
  return (
    <span className={META}>
      {children}
      <span>{labelOf(NOTIFICATION_KIND_LABEL, row.kind)}</span>
      <span>{formatDateTime(row.createdAt)}</span>
      {row.readAt === null && <StatusBadge tone="warn">Chưa đọc</StatusBadge>}
      {row.itemDeleted && <span>Việc đã xóa</span>}
    </span>
  );
}

/** The buttons sit above the stretched title link, wrap rather than scroll, and reach 44 px under `max-md`. */
const ACTS = 'relative z-10 flex flex-wrap items-center gap-2 pt-1 max-md:[&_.wp-btn]:min-h-11';

/**
 * Once a row's buttons go (the answer was taken and the inbox read again), the focus they held would fall to the page:
 * it lands on the row's own link instead.
 */
function useFocusAfterAnswer(actionable: boolean, link: RefObject<HTMLAnchorElement | null>): void {
  const was = useRef(actionable);
  useEffect(() => {
    const lost = was.current && !actionable;
    was.current = actionable;
    if (!lost) return;
    const active = document.activeElement;
    if (active === null || active === document.body) link.current?.focus();
  }, [actionable, link]);
}

/** §7.1, mockup §2: "{Bình} giao cho bạn {thẻ}", "Hạn {ngày}", and the assignee's three buttons while actionable. */
function AssignmentRow({ row, onOpen }: { row: ItemNotificationDTO; onOpen: (row: ItemNotificationDTO) => void }) {
  const d = row.delegation!;
  const link = useRef<HTMLAnchorElement>(null);
  const actionable = d.actionable && d.deadline !== null && !row.itemDeleted;
  useFocusAfterAnswer(actionable, link);
  return (
    <li className={cn(ROW, row.readAt === null && UNREAD_ROW)}>
      <KindTileIcon kind={row.kind} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link ref={link} to={hrefOf(row)} onClick={() => { onOpen(row); }} className={cn(TITLE_LINK, 'font-normal')}>
          <Actor name={row.actorName} assisted={false} />
          {' giao cho bạn '}
          <strong className="font-bold">{row.title}</strong>
        </Link>
        <RowMeta row={row}>{d.deadline !== null && <span>{`Hạn ${dueDate(d.deadline)}`}</span>}</RowMeta>
        {actionable && (
          <div className={ACTS}>
            <RespondControls
              itemId={row.itemId} round={d.round} version={d.version} deadline={d.deadline!} state="waiting"
              stateBefore={null}
            />
          </div>
        )}
      </div>
    </li>
  );
}

/** The words of a delegation row after the person's name (§7.2, mockup §3, D86). */
function delegationWords(row: DelegationNotificationDTO): ReactNode {
  const title = <strong className="font-bold">{row.title}</strong>;
  const to = row.requestedDeadline === null ? null : <strong className="font-bold">{dayMonth(row.requestedDeadline)}</strong>;
  switch (row.signalKind) {
    case 'accepted': return <>{' đã nhận '}{title}</>;
    case 'deadline_requested':
      return (
        <>
          {' xin lùi '}{title}
          {row.previousDeadline !== null && ` từ ${dayMonth(row.previousDeadline)}`}
          {to !== null && <>{' tới '}{to}</>}
        </>
      );
    case 'question': return <>{' hỏi lại trên '}{title}</>;
    case 'deadline_accepted':
      return (
        <>
          {' đồng ý lùi '}{title}
          {row.requestedDeadline !== null && ` tới ${dayMonth(row.requestedDeadline)}`}
        </>
      );
    case 'deadline_declined': return <>{' không đồng ý lùi '}{title}</>;
    default: return <>{' · '}{title}</>;
  }
}

/**
 * §7.2: the other party's answer. A deadline request carries Chấp nhận / Từ chối while it is still this very request,
 * then reads how it ended (D85); a question quotes its excerpt; "An ✦" when An pressed through an assistant.
 */
function DelegationRow({ row, onOpen }: {
  row: DelegationNotificationDTO; onOpen: (row: DelegationNotificationDTO) => void;
}) {
  const link = useRef<HTMLAnchorElement>(null);
  const actionable = row.actionable && !row.itemDeleted;
  useFocusAfterAnswer(actionable, link);
  const outcome = row.resolution === null ? null : RESOLUTION_WORD[row.resolution];
  const note = row.signalKind === 'deadline_requested' && row.note !== null && row.note !== '' ? row.note : null;
  return (
    <li className={cn(ROW, row.readAt === null && UNREAD_ROW)}>
      <KindTileIcon kind={row.kind} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link ref={link} to={hrefOf(row)} onClick={() => { onOpen(row); }} className={cn(TITLE_LINK, 'font-normal')}>
          <Actor name={row.actorName} assisted={row.assisted} />
          {delegationWords(row)}
        </Link>
        {note !== null && <Excerpt text={note} />}
        {row.signalKind === 'question' && row.excerpt !== null && <Excerpt text={row.excerpt} deleted={row.commentDeleted} />}
        <RowMeta row={row}>
          {!actionable && outcome !== null && <StatusBadge tone={outcome.tone}>{outcome.word}</StatusBadge>}
        </RowMeta>
        {actionable && (
          <div className={ACTS}>
            <DecideButtons
              itemId={row.itemId} round={row.round} version={row.version} requestedDeadline={row.requestedDeadline}
            />
          </div>
        )}
      </div>
    </li>
  );
}

/** §7.3: "{Bình} bình luận trên {thẻ}" and its excerpt; opens the card. */
function CommentRow({ row, onOpen }: { row: CommentNotificationDTO; onOpen: (row: CommentNotificationDTO) => void }) {
  return (
    <li className={cn(ROW, row.readAt === null && UNREAD_ROW)}>
      <KindTileIcon kind={row.kind} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link to={hrefOf(row)} onClick={() => { onOpen(row); }} className={cn(TITLE_LINK, 'font-normal')}>
          <Actor name={row.actorName} assisted={row.assisted} />
          {' bình luận trên '}
          <strong className="font-bold">{row.title}</strong>
        </Link>
        <Excerpt text={row.excerpt} deleted={row.commentDeleted} />
        <RowMeta row={row} />
      </div>
    </li>
  );
}

/** D84: "{Bình} bình luận {n} lần trên {thẻ}" with the newest excerpt; opening it marks every member read. */
function CommentGroupRow({ group, onOpen }: {
  group: InboxCommentGroup; onOpen: (group: InboxCommentGroup) => void;
}) {
  const head = group.rows[0]!;
  return (
    <li className={cn(ROW, UNREAD_ROW)}>
      <KindTileIcon kind="comment" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link to={hrefOf(head)} onClick={() => { onOpen(group); }} className={cn(TITLE_LINK, 'font-normal')}>
          <Actor name={group.actorName} assisted={head.assisted} />
          {` bình luận ${group.rows.length} lần trên `}
          <strong className="font-bold">{group.title}</strong>
        </Link>
        <Excerpt text={head.excerpt} deleted={head.commentDeleted} />
        <RowMeta row={head} />
      </div>
    </li>
  );
}

export function NotificationMenu() {
  const queryClient = useQueryClient();
  const assistant = useAssistantOptional();
  const [open, setOpen] = useState(false);
  // Following a row goes somewhere; on a phone the panel would sit over the page it just opened (orchestrator
  // ruling, S2 P2 G7). Any change of location closes it, whatever row kind moved it. Adjusted during render, so
  // the page it lands on never paints one frame under an open panel.
  const location = useLocation();
  const here = `${location.pathname}${location.search}${location.hash}`;
  const [seen, setSeen] = useState(here);
  if (seen !== here) {
    setSeen(here);
    if (open) setOpen(false);
  }
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  // The panel fades out rather than vanishing, inert while it goes (hooks/use-presence.ts, styles/motion.css).
  const panel = usePresence<HTMLDivElement>(open);
  /** Rows this browser has already marked; opening the same entry twice must not write twice. */
  const marked = useRef(new Set<Id>());

  const count = useQuery<{ count: number }>({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: () => api<{ count: number }>('/api/notifications/unread-count'),
    refetchInterval: NOTIFICATION_POLL_MS,
    // A tab nobody is looking at polls nothing; coming back to it is what asks again.
    refetchIntervalInBackground: false,
    retry: false,
  });

  const inbox = useQuery<Page<NotificationDTO>>({
    queryKey: ['notifications', 'list', unreadOnly],
    queryFn: () => api<Page<NotificationDTO>>(`/api/notifications${unreadOnly ? '?unread=true' : ''}`),
    enabled: open,
    retry: false,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [queryClient]);

  useEffect(() => {
    const onReturn = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [refresh]);

  /** S7 §9.4 (D93, D102): a run notice closes the bell and opens Hana on her chat view, the tray unfolded. */
  function openTray(): void {
    setOpen(false);
    if (assistant === null) return;
    unfoldTray(assistant.principal.userId);
    assistant.showTray();
  }

  async function openRow(row: NotificationDTO): Promise<void> {
    if (row.readAt !== null || marked.current.has(row.id)) return;
    marked.current.add(row.id);
    try {
      await api(`/api/notifications/${row.id}/read`, { method: 'POST', body: {} });
    } catch {
      // Navigation has already begun and the entry is still in the inbox; a refused mark is not worth
      // interrupting the person for, and the next read of the list shows the truth.
      marked.current.delete(row.id);
      return;
    }
    refresh();
  }

  /** The unread members of a group in one write; a group already read, or already sent, writes nothing. */
  async function openGroup(group: { rows: readonly NotificationDTO[] }): Promise<void> {
    const ids = group.rows.filter((row) => row.readAt === null && !marked.current.has(row.id)).map((row) => row.id);
    if (ids.length === 0) return;
    for (const id of ids) marked.current.add(id);
    try {
      await api('/api/notifications/read', { method: 'POST', body: { ids } });
    } catch {
      for (const id of ids) marked.current.delete(id);
      return;
    }
    refresh();
  }

  async function markAll(): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      await api('/api/notifications/read-all', { method: 'POST', body: {} });
      refresh();
    } catch {
      // Same reasoning: the badge keeps its number, which is the honest answer to a refused write.
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Escape' || !open) return;
    // A row's popover answers Escape first (Radix, on the document in the capture phase) and marks it handled: that
    // Escape closes only the popover, as in `ModalDialog` (components/ui/popover.tsx).
    if (event.defaultPrevented) return;
    setOpen(false);
    toggle.current?.focus();
  }

  const unread = count.data?.count ?? 0;
  const rows = inbox.data?.items;
  const entries = useMemo(() => groupInbox(rows ?? []), [rows]);

  return (
    // Below `md` the wrapper steps out of positioning, so the panel hangs off the sticky shell header rather
    // than off the bell: the bell moves with the header's actions, and a panel pinned to it moved off the
    // screen with it (x = -29.5 at 390x844, x = -164 at 320 wide, on the old band).
    <div className="relative max-md:static" onKeyDown={onKeyDown}>
      {/* The HyperTek prototype's header bell (h-prototype `components/layout/header.tsx`), class for class:
          a 40 px icon button — 44 px to hit, through the touch floor — with the unread count as a red pill
          on its corner. The count stays inside the button, and its aria-label says it in words. */}
      <button
        type="button" ref={toggle} data-primary-action
        className="relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-[hsl(var(--h-muted))] aria-expanded:bg-[hsl(var(--h-muted))]"
        aria-expanded={open} aria-controls={PANEL_ID}
        aria-label={unread > 0 ? `Thông báo, ${unread} chưa đọc` : 'Thông báo, không có mục chưa đọc'}
        onClick={() => setOpen((value) => !value)}
      >
        <Bell className="h-5 w-5 text-[hsl(var(--h-card-foreground))]" aria-hidden="true" />
        {unread > 0 && (
          // White on --h-error measures 4.91:1.
          <span
            className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[hsl(var(--h-error))] px-1 text-[10px] font-bold tabular-nums text-white"
            aria-hidden="true"
          >
            {unread}
          </span>
        )}
      </button>

      {panel.mounted && (
        <div
          ref={panel.ref} data-state={panel.state} inert={panel.state === 'closed'}
          id={PANEL_ID} role="group" aria-label="Thông báo của bạn"
          className={cn(
            'h-pop absolute right-0 top-[calc(100%+0.5rem)] z-40 flex w-[min(380px,calc(100vw-1.5rem))] flex-col',
            'max-h-[min(70vh,560px)] overflow-hidden rounded-xl border border-[hsl(var(--h-border))] bg-white',
            'text-[hsl(var(--h-card-foreground))] shadow-[var(--h-shadow-lg)]',
            'max-md:left-3 max-md:right-3 max-md:top-[calc(100%+0.25rem)] max-md:w-auto',
          )}
        >
          <div className="flex flex-wrap items-center gap-2 border-b border-[hsl(var(--h-border))] px-3 py-2">
            <Button
              type="button" variant="ghost" size="sm" aria-pressed={unreadOnly}
              className="text-[hsl(var(--h-muted-foreground))] aria-pressed:bg-[hsl(var(--h-primary)/0.1)] aria-pressed:font-semibold aria-pressed:text-[hsl(var(--h-primary))]"
              onClick={() => setUnreadOnly((value) => !value)}
            >
              <ListFilter aria-hidden="true" />
              Chỉ chưa đọc
            </Button>
            <Button
              type="button" data-primary-action variant="ghost" size="sm"
              className="ml-auto text-[hsl(var(--h-primary))]" disabled={busy || unread === 0}
              onClick={() => { void markAll(); }}
            >
              <CheckCheck aria-hidden="true" />
              Đánh dấu tất cả đã đọc
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {inbox.isPending
              ? <p className="px-4 py-8 text-center text-sm text-[hsl(var(--h-muted-foreground))]">Đang tải…</p>
              : entries.length === 0
                ? (
                  <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
                    <span
                      className="flex size-10 items-center justify-center rounded-full bg-[hsl(var(--h-muted))] text-[hsl(var(--h-muted-foreground))]"
                      aria-hidden="true"
                    >
                      <BellOff className="size-5" />
                    </span>
                    <p className="text-sm text-[hsl(var(--h-muted-foreground))]">Chưa có thông báo nào</p>
                  </div>
                )
                : (
                  <ul className="divide-y divide-[hsl(var(--h-border))]">
                    {entries.map((entry) => {
                      if (entry.kind === 'group') {
                        return (
                          <GroupRow
                            key={entry.key} group={entry} actorName={entry.actorName ?? UNKNOWN_ACTOR}
                            onOpenGroup={(group) => { void openGroup(group); }}
                            onOpenRow={(row) => { void openRow(row); }}
                          />
                        );
                      }
                      if (entry.kind === 'comments') {
                        return <CommentGroupRow key={entry.key} group={entry} onOpen={(g) => { void openGroup(g); }} />;
                      }
                      const { row } = entry;
                      if (row.kind === 'delegation_response' || row.kind === 'delegation_decision') {
                        return <DelegationRow key={row.id} row={row} onOpen={(r) => { void openRow(r); }} />;
                      }
                      if (row.kind === 'comment') {
                        return <CommentRow key={row.id} row={row} onOpen={(r) => { void openRow(r); }} />;
                      }
                      if (row.kind === 'assignment' && row.delegation !== undefined) {
                        return <AssignmentRow key={row.id} row={row} onOpen={(r) => { void openRow(r); }} />;
                      }
                      if (row.kind === 'assistant_pending') {
                        return <ActionRow key={row.id} row={row} onOpen={(r) => { void openRow(r); }} />;
                      }
                      if (row.kind === 'nudge') {
                        return <NudgeRow key={row.id} row={row} onOpen={(r) => { void openRow(r); }} />;
                      }
                      if (row.kind === 'ext_connection_expired' || row.kind === 'ext_tools_review') {
                        return <ExtRow key={row.id} row={row} onOpen={(r) => { void openRow(r); }} />;
                      }
                      if (row.kind === 'assistant_run') {
                        return <RunRow key={row.id} row={row} onOpen={(r) => { openTray(); void openRow(r); }} />;
                      }
                      if (row.kind === 'onboarding_reminder') {
                        return <OnboardingRow key={row.id} row={row} onOpen={(r) => { void openRow(r); }} />;
                      }
                      const inRoot = rootNote(row);
                      return (
                        // The title link is stretched over its row: the whole entry is the thumb's target, and
                        // the one link inside it is still the only thing a keyboard or a screen reader meets.
                        <li key={row.id} className={cn(ROW, row.readAt === null && UNREAD_ROW)}>
                          <KindTileIcon kind={row.kind} />
                          <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <Link to={hrefOf(row)} onClick={() => { void openRow(row); }} className={TITLE_LINK}>
                              {row.title}
                            </Link>
                            {inRoot !== null && (
                              <span className="text-sm text-[hsl(var(--h-muted-foreground))] wrap-anywhere">{inRoot}</span>
                            )}
                            <span className={META}>
                              <span>{labelOf(NOTIFICATION_KIND_LABEL, row.kind)}</span>
                              <span>{formatDateTime(row.createdAt)}</span>
                              {row.readAt === null && <StatusBadge tone="warn">Chưa đọc</StatusBadge>}
                              {row.itemDeleted && <span>Việc đã xóa</span>}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
          </div>
        </div>
      )}
    </div>
  );
}
